/**
 * QMS Ticket Timeline Component
 * Displays activity/audit trail for a QMS ticket fetched from the API
 */

import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  TrendingUp,
  Shield,
  Lock,
  FileText,
  Check,
  Ban,
  CheckSquare,
  MessageSquare,
  Trash2,
  UserPlus,
  RefreshCw,
  AlertCircle,
  Paperclip,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useQmsTimeline } from '../../hooks/useQmsTimeline';
import { Skeleton } from '@/components/ui/skeleton';

interface AiInvestigationMetadata {
  summary?: string;
  riskAssessment?: {
    level: string;
    reasoning: string;
    factors?: string[];
  };
  potentialRootCauses?: Array<{
    cause: string;
    confidence: string;
    explanation: string;
  }>;
  recommendedActions?: string[];
  suggestedTags?: string[];
  suggestedPriority?: number;
  modelId?: string;
}

interface TimelineEvent {
  date: string;
  type: string;
  label: string;
  userName?: string;
  notes?: string;
  icon: React.ReactNode;
  color: string;
  riskLevel?: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  aiInvestigation?: AiInvestigationMetadata;
}

interface QmsTicketTimelineProps {
  ticketId: string;
}

export function QmsTicketTimeline({ ticketId }: QmsTicketTimelineProps) {
  const { t } = useTranslation('qms');
  const { data: timelineData, isLoading, error } = useQmsTimeline(ticketId);

  // Map API event to display event with icon and color
  const mapEventToDisplay = (event: {
    type: string;
    timestamp: string;
    user?: { _id?: string; firstname?: string; lastname?: string };
    notes?: string;
    status?: string;
    riskLevel?: string;
    fieldName?: string;
    oldValue?: string;
    newValue?: string;
    content?: string;
    decision?: string;
    needsCapa?: boolean;
    metadata?: {
      contentPreview?: string;
      deletedContentPreview?: string;
      // AI Investigation metadata
      summary?: string;
      riskAssessment?: { level: string; reasoning: string; factors?: string[] };
      potentialRootCauses?: Array<{ cause: string; confidence: string; explanation: string }>;
      recommendedActions?: string[];
      suggestedTags?: string[];
      suggestedPriority?: number;
      modelId?: string;
    };
  }): TimelineEvent => {
    const getUserName = (): string => {
      if (!event.user) return t('timeline.system');
      const { firstname, lastname } = event.user;
      if (firstname || lastname) {
        return `${firstname || ''} ${lastname || ''}`.trim();
      }
      return t('timeline.system');
    };

    // Default values
    let icon = <Clock className="h-5 w-5 text-gray-600" />;
    let color = 'bg-gray-100 border-gray-300';
    let label = event.type;
    let notes = event.notes;

    switch (event.type) {
      case 'created':
        icon = <FileText className="h-5 w-5 text-blue-600" />;
        color = 'bg-blue-100 border-blue-300';
        label = t('timeline.events.created');
        break;

      case 'intake_decision':
        if (event.decision === 'create_ticket') {
          icon = <Check className="h-5 w-5 text-green-600" />;
          color = 'bg-green-100 border-green-300';
          label = t('timeline.events.intakeAccepted');
        } else {
          icon = <Ban className="h-5 w-5 text-red-600" />;
          color = 'bg-red-100 border-red-300';
          label = t('timeline.events.intakeRejected');
        }
        break;

      case 'qd_approved':
      case 'qd_approval':
        icon = <CheckCircle className="h-5 w-5 text-green-600" />;
        color = 'bg-green-100 border-green-300';
        label = t('timeline.events.qdApproved');
        break;

      case 'qd_rejected':
        icon = <XCircle className="h-5 w-5 text-red-600" />;
        color = 'bg-red-100 border-red-300';
        label = t('timeline.events.qdRejected');
        break;

      case 'qd_changes_requested':
        icon = <Edit className="h-5 w-5 text-orange-600" />;
        color = 'bg-orange-100 border-orange-300';
        label = t('timeline.events.changesRequested');
        break;

      case 'ceo_reviewed':
      case 'ceo_confirmed':
        icon = <Shield className="h-5 w-5 text-purple-600" />;
        color = 'bg-purple-100 border-purple-300';
        label = t('timeline.events.ceoConfirmed');
        break;

      case 'ceo_priority_adjusted':
        icon = <Shield className="h-5 w-5 text-purple-600" />;
        color = 'bg-purple-100 border-purple-300';
        label = t('timeline.events.ceoAdjusted');
        break;

      case 'capa_decision':
        icon = event.needsCapa
          ? <AlertCircle className="h-5 w-5 text-orange-600" />
          : <CheckCircle className="h-5 w-5 text-green-600" />;
        color = event.needsCapa ? 'bg-orange-100 border-orange-300' : 'bg-green-100 border-green-300';
        label = event.needsCapa
          ? t('timeline.events.capaRequired')
          : t('timeline.events.capaNotRequired');
        break;

      case 'change_control_approved':
        icon = <CheckSquare className="h-5 w-5 text-green-600" />;
        color = 'bg-green-100 border-green-300';
        label = t('timeline.events.changeApproved');
        break;

      case 'completed':
        icon = <TrendingUp className="h-5 w-5 text-blue-600" />;
        color = 'bg-blue-100 border-blue-300';
        label = t('timeline.events.completed');
        break;

      case 'validated':
        icon = <Lock className="h-5 w-5 text-gray-700" />;
        color = 'bg-gray-100 border-gray-300';
        label = t('timeline.events.validated');
        break;

      case 'comment_added':
        icon = <MessageSquare className="h-5 w-5 text-blue-600" />;
        color = 'bg-blue-100 border-blue-300';
        label = t('timeline.events.commentAdded');
        notes = event.content || event.metadata?.contentPreview;
        break;

      case 'comment_edited':
        icon = <Edit className="h-5 w-5 text-yellow-600" />;
        color = 'bg-yellow-100 border-yellow-300';
        label = t('timeline.events.commentEdited');
        break;

      case 'comment_deleted':
        icon = <Trash2 className="h-5 w-5 text-red-600" />;
        color = 'bg-red-100 border-red-300';
        label = t('timeline.events.commentDeleted');
        notes = event.metadata?.deletedContentPreview;
        break;

      case 'assigned':
        icon = <UserPlus className="h-5 w-5 text-indigo-600" />;
        color = 'bg-indigo-100 border-indigo-300';
        label = t('timeline.events.assigned');
        break;

      case 'status_changed':
        icon = <RefreshCw className="h-5 w-5 text-teal-600" />;
        color = 'bg-teal-100 border-teal-300';
        label = t('timeline.events.statusChanged');
        break;

      case 'field_updated':
        icon = <Edit className="h-5 w-5 text-gray-600" />;
        color = 'bg-gray-100 border-gray-300';
        label = t('timeline.events.fieldUpdated');
        break;

      case 'attachment_added':
        icon = <Paperclip className="h-5 w-5 text-green-600" />;
        color = 'bg-green-100 border-green-300';
        label = t('timeline.events.attachmentAdded');
        break;

      case 'attachment_deleted':
        icon = <Paperclip className="h-5 w-5 text-red-600" />;
        color = 'bg-red-100 border-red-300';
        label = t('timeline.events.attachmentDeleted');
        break;

      case 'ai_findings_accepted':
        icon = <Sparkles className="h-5 w-5 text-purple-600" />;
        color = 'bg-purple-100 border-purple-300';
        label = t('timeline.events.aiFindingsAccepted');
        break;

      default:
        // Use event type as label if no translation available
        label = event.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }

    return {
      date: event.timestamp,
      type: event.type,
      label,
      userName: getUserName(),
      notes,
      icon,
      color,
      riskLevel: event.riskLevel,
      fieldName: event.fieldName,
      oldValue: event.oldValue,
      // Include AI investigation metadata if present
      aiInvestigation: event.metadata?.summary ? {
        summary: event.metadata.summary,
        riskAssessment: event.metadata.riskAssessment,
        potentialRootCauses: event.metadata.potentialRootCauses,
        recommendedActions: event.metadata.recommendedActions,
        suggestedTags: event.metadata.suggestedTags,
        suggestedPriority: event.metadata.suggestedPriority,
        modelId: event.metadata.modelId,
      } : undefined,
      newValue: event.newValue,
    };
  };

  // Sort events by date (most recent first)
  const events = (timelineData || [])
    .map(mapEventToDisplay)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {t('timeline.title')}
        </h3>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {t('timeline.title')}
        </h3>
        <div className="text-center py-8 text-red-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-400" />
          <p>{t('timeline.loadError')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Clock className="h-5 w-5" />
        {t('timeline.title')}
        {events.length > 0 && (
          <span className="text-sm font-normal text-gray-500">({events.length})</span>
        )}
      </h3>

      {events.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>{t('timeline.noActivity')}</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {/* Timeline events */}
          <div className="space-y-6">
            {events.map((event, index) => (
              <div key={`${event.type}-${index}`} className="relative flex gap-4">
                {/* Event icon */}
                <div
                  className={`flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center z-10 ${event.color}`}
                >
                  {event.icon}
                </div>

                {/* Event content */}
                <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900">
                        {event.label}
                      </h4>
                      {event.userName && (
                        <p className="text-sm text-gray-600 mt-1">
                          {t('timeline.by')} {event.userName}
                        </p>
                      )}
                    </div>
                    <span className="text-sm text-gray-500 flex-shrink-0">
                      {format(new Date(event.date), 'MMM d, yyyy • h:mm a')}
                    </span>
                  </div>

                  {/* Field change details */}
                  {event.fieldName && (
                    <div className="text-sm text-gray-700 mt-2 bg-gray-50 rounded p-3">
                      <span className="font-medium">{event.fieldName}:</span>
                      {event.oldValue && (
                        <span className="text-red-600 line-through ml-2">
                          {String(event.oldValue).substring(0, 50)}
                          {String(event.oldValue).length > 50 ? '...' : ''}
                        </span>
                      )}
                      {event.newValue && (
                        <span className="text-green-600 ml-2">
                          → {String(event.newValue).substring(0, 50)}
                          {String(event.newValue).length > 50 ? '...' : ''}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Event notes/details */}
                  {event.notes && !event.fieldName && (
                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap bg-gray-50 rounded p-3">
                      {event.notes}
                    </p>
                  )}

                  {/* Risk level for CEO reviews */}
                  {event.riskLevel && (
                    <div className="mt-2">
                      <span className="text-sm font-medium text-gray-700">
                        {t('ceoReview.riskLevel')}:{' '}
                      </span>
                      <span className={`text-sm font-semibold ${
                        event.riskLevel === 'critical' ? 'text-red-600' :
                        event.riskLevel === 'high' ? 'text-orange-600' :
                        event.riskLevel === 'medium' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {event.riskLevel.toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* AI Investigation details */}
                  {event.aiInvestigation && (
                    <div className="mt-3 space-y-3 bg-purple-50 rounded-lg p-3 border border-purple-200">
                      {/* Summary */}
                      {event.aiInvestigation.summary && (
                        <div>
                          <p className="text-xs font-semibold text-purple-800 mb-1">
                            {t('timeline.aiInvestigation.summary')}
                          </p>
                          <p className="text-sm text-gray-700">{event.aiInvestigation.summary}</p>
                        </div>
                      )}

                      {/* Risk Assessment */}
                      {event.aiInvestigation.riskAssessment && (
                        <div>
                          <p className="text-xs font-semibold text-purple-800 mb-1">
                            {t('timeline.aiInvestigation.riskAssessment')}
                          </p>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              variant="outline"
                              className={`capitalize text-xs ${
                                event.aiInvestigation.riskAssessment.level === 'critical' ? 'bg-red-100 text-red-800 border-red-200' :
                                event.aiInvestigation.riskAssessment.level === 'high' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                event.aiInvestigation.riskAssessment.level === 'medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                                'bg-green-100 text-green-800 border-green-200'
                              }`}
                            >
                              {event.aiInvestigation.riskAssessment.level}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{event.aiInvestigation.riskAssessment.reasoning}</p>
                          {event.aiInvestigation.riskAssessment.factors && event.aiInvestigation.riskAssessment.factors.length > 0 && (
                            <ul className="mt-1 list-disc list-inside text-xs text-gray-500">
                              {event.aiInvestigation.riskAssessment.factors.map((factor, i) => (
                                <li key={i}>{factor}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Root Causes */}
                      {event.aiInvestigation.potentialRootCauses && event.aiInvestigation.potentialRootCauses.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-purple-800 mb-1">
                            {t('timeline.aiInvestigation.rootCauses')}
                          </p>
                          <ul className="space-y-1">
                            {event.aiInvestigation.potentialRootCauses.map((cause, i) => (
                              <li key={i} className="text-sm text-gray-700">
                                <span className="font-medium">{cause.cause}</span>
                                <span className="text-xs text-gray-500 ml-1">({cause.confidence})</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Recommended Actions */}
                      {event.aiInvestigation.recommendedActions && event.aiInvestigation.recommendedActions.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-purple-800 mb-1">
                            {t('timeline.aiInvestigation.recommendedActions')}
                          </p>
                          <ul className="list-disc list-inside text-sm text-gray-700">
                            {event.aiInvestigation.recommendedActions.map((action, i) => (
                              <li key={i}>{action}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Suggested Tags */}
                      {event.aiInvestigation.suggestedTags && event.aiInvestigation.suggestedTags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {event.aiInvestigation.suggestedTags.map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Model info */}
                      {event.aiInvestigation.modelId && (
                        <p className="text-xs text-gray-400 pt-1 border-t border-purple-200">
                          Model: {event.aiInvestigation.modelId}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
